import { useEffect, useRef } from 'react'
import { useAui, useAuiState } from '@assistant-ui/react'
import { setActiveRemoteThreadId } from '@/runtime/activeThreadId'

export const ACTIVE_THREAD_STORAGE_KEY = 'inquisitor.activeThreadRemoteId'

/**
 * Persist the active remote thread across Settings ↔ Chat navigations
 * and hard reloads. Runtime is shared in-app; this covers remount/reload.
 */
export function ThreadSelectionPersistence() {
  const aui = useAui()
  const restoredRef = useRef(false)
  const mainThreadId = useAuiState((state) => state.threads.mainThreadId)
  const threadIds = useAuiState((state) => state.threads.threadIds)
  const threadItems = useAuiState((state) => state.threads.threadItems)
  const isLoading = useAuiState((state) => state.threads.isLoading)

  useEffect(() => {
    if (!mainThreadId) return
    try {
      const remoteId = aui.threads().item({ id: mainThreadId }).getState()
        .remoteId
      if (remoteId) {
        setActiveRemoteThreadId(remoteId)
        sessionStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, remoteId)
      }
    } catch {
      // Main thread metadata may not be ready yet.
    }
  }, [aui, mainThreadId, threadItems])

  useEffect(() => {
    if (restoredRef.current || isLoading) return
    const saved = sessionStorage.getItem(ACTIVE_THREAD_STORAGE_KEY)
    if (!saved || threadIds.length === 0) return

    for (const threadId of threadIds) {
      try {
        const item = aui.threads().item({ id: threadId }).getState()
        if (item.remoteId !== saved) continue
        restoredRef.current = true
        setActiveRemoteThreadId(saved)
        if (threadId !== mainThreadId) {
          void aui.threads().switchToThread(threadId)
        }
        return
      } catch {
        // Thread item may not be ready yet.
      }
    }
  }, [aui, isLoading, mainThreadId, threadIds, threadItems])

  return null
}
